import { makeAutoObservable, runInAction } from 'mobx'
import agent from '../api/agent'
import { Firearm, FirearmFormValues } from '../models/firearm'

export default class FirearmStore {
  firearmRegistry = new Map<string, Firearm>()
  selectedFirearm: Firearm | undefined = undefined
  loading = false
  loadingInitial = false

  constructor () {
    makeAutoObservable(this)
  }

  get firearmsByDate () {
    return Array.from(this.firearmRegistry.values()).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  }

  loadFirearms = async () => {
    this.loadingInitial = true
    try {
      const firearms = await agent.Firearms.list()
      firearms.forEach((f) => this.setFirearm(f))
      runInAction(() => (this.loadingInitial = false))
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loadingInitial = false))
    }
  }

  loadFirearm = async (id: string) => {
    let firearm = this.firearmRegistry.get(id)
    if (firearm) {
      this.selectedFirearm = firearm
      return firearm
    }
    this.loadingInitial = true
    try {
      firearm = await agent.Firearms.details(id)
      this.setFirearm(firearm)
      runInAction(() => {
        this.selectedFirearm = firearm
        this.loadingInitial = false
      })
      return firearm
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loadingInitial = false))
    }
  }

  private setFirearm = (firearm: Firearm) => {
    this.firearmRegistry.set(firearm.id, firearm)
  }

  createFirearm = async (firearm: FirearmFormValues) => {
    this.loading = true
    try {
      const result = await agent.Firearms.create(firearm)
      runInAction(() => (this.loading = false))
      return result?.id
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loading = false))
    }
  }

  updateFirearm = async (firearm: FirearmFormValues) => {
    this.loading = true
    try {
      await agent.Firearms.update(firearm)
      runInAction(() => {
        if (firearm.id) {
          const updated = { ...this.firearmRegistry.get(firearm.id), ...firearm } as Firearm
          this.firearmRegistry.set(firearm.id, updated)
          this.selectedFirearm = updated
        }
        this.loading = false
      })
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loading = false))
    }
  }

  deleteFirearm = async (id: string) => {
    this.loading = true
    try {
      await agent.Firearms.delete(id)
      runInAction(() => {
        this.firearmRegistry.delete(id)
        this.selectedFirearm = undefined
        this.loading = false
      })
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loading = false))
    }
  }

  uploadImage = async (firearmId: string, file: File) => {
    try {
      const image = await agent.Firearms.addImage(firearmId, file)
      runInAction(() => {
        const firearm = this.firearmRegistry.get(firearmId) ?? this.selectedFirearm
        if (firearm) {
          firearm.images.push(image)
          if (image.isMain) firearm.images.forEach((i) => (i.isMain = i.id === image.id))
          this.firearmRegistry.set(firearm.id, firearm)
          if (this.selectedFirearm?.id === firearm.id) this.selectedFirearm = firearm
        }
      })
      return image
    } catch (error) {
      console.error(error)
    }
  }

  deleteImage = async (firearmId: string, imageId: string) => {
    try {
      await agent.Firearms.deleteImage(firearmId, imageId)
      runInAction(() => {
        const firearm = this.firearmRegistry.get(firearmId) ?? this.selectedFirearm
        if (firearm) {
          firearm.images = firearm.images.filter((i) => i.id !== imageId)
          this.firearmRegistry.set(firearm.id, firearm)
          if (this.selectedFirearm?.id === firearm.id) this.selectedFirearm = firearm
        }
      })
    } catch (error) {
      console.error(error)
    }
  }

  setMainImage = async (firearmId: string, imageId: string) => {
    try {
      await agent.Firearms.setMainImage(firearmId, imageId)
      runInAction(() => {
        const firearm = this.firearmRegistry.get(firearmId) ?? this.selectedFirearm
        if (firearm) {
          firearm.images.forEach((i) => (i.isMain = i.id === imageId))
          this.firearmRegistry.set(firearm.id, firearm)
          if (this.selectedFirearm?.id === firearm.id) this.selectedFirearm = firearm
        }
      })
    } catch (error) {
      console.error(error)
    }
  }

  clearSelectedFirearm = () => {
    this.selectedFirearm = undefined
  }
}
